import { call, put, takeEvery, select } from 'redux-saga/effects';
import { apiGet, apiPost, apiPatch } from '../core/dapi';
import { getLocationUrl } from '../core/dapi/location';
import { receiveLocation } from '../actions/location';
import { bookingError, receiveBooking } from '../actions/account';
import { getBookingById } from '../core/dapi/newBooking';
import {
  bookingNotesError,
  bookingNotesFetching,
  bookingNotesReceived,
  bookingNotesSubmitting,
} from '../ducks/booking/notes';
import { getBookingNotesUrl } from '../core/dapi/bookingNotes';
import { showModal } from '../actions/runtime';
import { MARY_MAGIC_SURVEY_MODAL } from '../constants/modalIds';
import { delay } from 'redux-saga';
import { safeGet } from '../core/util/object';
import { isAccountInMarketPlace } from '../core/util/account';
import { addSource } from '../ducks/maryMagicSurvey';
import { SOURCE_PVS } from '../components/MaryMagicSurvey/constants';
import { bookingImagesError, bookingImagesReceived } from '../ducks/booking/images';
import { AccountFileType } from '../constants';
import { getAccountImagesUrl } from '../core/util/account';

class PostVisitSummarySagaActions {
  static CLEAR_BOOKING_NOTES = 'saga/PVS_CLEAR_DATA';
  static FETCH_BOOKING_NOTES = 'saga/PVS_FETCH_BOOKING_NOTES';
  static FETCH_DATA = 'saga/PVS_FETCH_DATA';
  static FETCH_IMAGES = 'saga/PVS_FETCH_IMAGES';
  static SAVE_BOOKING_NOTES = 'saga/PVS_SAVE_BOOKING_NOTES';
  static UPDATE_BOOKING_NOTES = 'saga/PVS_UPDATE_BOOKING_NOTES';
}

function* fetchImages({
  accountId,
  bookingId,
  userProfileId,
  imageType = AccountFileType.PVS_PHOTO,
}) {
  try {
    const url = getAccountImagesUrl({
      bookingId,
      accountId,
      userProfileId,
      imageType,
    });
    const bookingImagesResponse = yield call(apiGet, url);
    yield put(bookingImagesReceived({ bookingId, ...bookingImagesResponse }));
  } catch (e) {
    yield put(bookingImagesError({ bookingId, ...e }));
  }
}

function* fetchData({ bookingId }) {
  try {
    const bookingResponse = yield call(apiGet, getBookingById(bookingId));
    yield put(receiveBooking(bookingResponse));

    const locationResponse = yield call(apiGet, getLocationUrl(bookingResponse.location_id));

    yield put(receiveLocation(locationResponse));
  } catch (e) {
    yield put(bookingError(e));
  }
}

function* fetchNotes({ bookingId }) {
  try {
    yield put(bookingNotesFetching({ bookingId }));

    const result = yield call(apiGet, getBookingNotesUrl(bookingId));
    yield put(bookingNotesReceived({ bookingId, result }));
  } catch (e) {
    yield put(bookingNotesError({ bookingId, ...e }));
  }
}

function* saveNotes({ bookingId, notes, prescriptionFilled, prescriptions, onSuccess }) {
  try {
    yield put(bookingNotesSubmitting({ bookingId, submitting: true }));
    const postData = {
      notes,
      prescription_filled: prescriptionFilled,
      prescriptions,
    };

    const result = yield call(apiPost, getBookingNotesUrl(bookingId), postData);
    yield put(bookingNotesReceived({ bookingId, result }));
    if (onSuccess) onSuccess();

    // open mary magic survey modal
    const accountSummary = yield select(state => safeGet(state)`account.summary`);
    if (isAccountInMarketPlace(accountSummary)) {
      yield call(delay, 1000);
      yield put(showModal(MARY_MAGIC_SURVEY_MODAL));
      yield put(addSource(SOURCE_PVS));
    }
  } catch (e) {
    yield put(bookingNotesError({ bookingId, message: e.message }));
  } finally {
    yield put(bookingNotesSubmitting({ bookingId, submitting: false }));
  }
}

function* updateNotes({ bookingId, notes, prescriptionFilled, prescriptions, onSuccess }) {
  try {
    yield put(bookingNotesSubmitting({ bookingId, submitting: true }));
    const postData = {
      notes,
      prescription_filled: prescriptionFilled,
      prescriptions,
    };

    const result = yield call(apiPatch, getBookingNotesUrl(bookingId), postData);
    yield put(bookingNotesReceived({ bookingId, result }));
    if (onSuccess) onSuccess();
  } catch (e) {
    yield put(bookingNotesError({ bookingId, message: e.message }));
  } finally {
    yield put(bookingNotesSubmitting({ bookingId, submitting: false }));
  }
}

function* clearNotes({ bookingId }) {
  yield put(bookingNotesReceived({ bookingId, result: null }));
}

function* rootSaga() {
  yield takeEvery(PostVisitSummarySagaActions.CLEAR_BOOKING_NOTES, clearNotes);
  yield takeEvery(PostVisitSummarySagaActions.FETCH_BOOKING_NOTES, fetchNotes);
  yield takeEvery(PostVisitSummarySagaActions.FETCH_IMAGES, fetchImages);
  yield takeEvery(PostVisitSummarySagaActions.FETCH_DATA, fetchData);
  yield takeEvery(PostVisitSummarySagaActions.SAVE_BOOKING_NOTES, saveNotes);
  yield takeEvery(PostVisitSummarySagaActions.UPDATE_BOOKING_NOTES, updateNotes);
}

export { PostVisitSummarySagaActions, rootSaga as default, fetchImages };
